import { IMAGE_TYPE } from '../../../domain/constants/src/Constants';
import styles from '../../../styles/src/styles';
import SmartCamera from '../../../domain/camera/src/SmartCamera';
import { t } from '../../../domain/localisation';

import './document-capture';
import './document-capture-review';
import './document-capture-instructions';
import './document-capture-submission';
import './document-auto-capture/index.ts';
import packageJson from '../../../../package.json';

const COMPONENTS_VERSION = packageJson.version;

// Attribute values are interpolated into the innerHTML template below. Document
// display names come from the catalog and legitimately contain apostrophes
// ("Driver's License") and non-ASCII text, so they must be escaped and
// double-quoted — a raw apostrophe inside a single-quoted attribute truncates it.
const escAttr = (s) =>
  String(s)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/</g, '&lt;');

const smartCameraWeb = document.querySelector('smart-camera-web');

async function getPermissions(captureScreen) {
  try {
    const stream = await SmartCamera.getMedia({
      audio: false,
      video: SmartCamera.environmentOptions,
    });
    const devices = await navigator.mediaDevices.enumerateDevices();
    const videoDevice = devices.find(
      (device) =>
        device.kind === 'videoinput' &&
        stream.getVideoTracks()[0].getSettings().deviceId === device.deviceId,
    );
    smartCameraWeb?.dispatchEvent(
      new CustomEvent('metadata.camera-name', {
        detail: { cameraName: videoDevice?.label },
      }),
    );
    captureScreen.removeAttribute('data-camera-error');
    captureScreen.setAttribute('data-camera-ready', true);
  } catch (error) {
    captureScreen.removeAttribute('data-camera-ready');
    captureScreen.setAttribute(
      'data-camera-error',
      SmartCamera.handleCameraError(error),
    );
  }
}

class DocumentCaptureScreens extends HTMLElement {
  constructor() {
    super();
    this.activeScreen = null;
    this.smartCameraWeb = this.closest('smart-camera-web');
    smartCameraWeb?.dispatchEvent(new CustomEvent('metadata.initialize'));
  }

  connectedCallback() {
    // Tag name to use for the live capture view. Opt-in via the
    // `auto-capture-enabled` attribute on <document-capture-screens>; defaults to the
    // legacy <document-capture> element so existing flows are untouched.
    const captureTag = this.autoCaptureFeature
      ? 'document-auto-capture'
      : 'document-capture';
    const instructionsTag = 'document-capture-instructions';
    this.innerHTML = `
      ${styles(this.themeColor)}
      <div style="height: 100%;">
      <${instructionsTag} ${this.instructionsThemeColor} id='document-capture-instructions-front' ${this.title} ${this.documentType} ${this.documentFormat} ${this.documentName}
      ${this.documentCaptureModes} ${this.showNavigation} ${this.hideInstructions ? 'hidden' : ''}
      ${this.hideAttribution}
      ></${instructionsTag}>
      <${captureTag} id='document-capture-front' side-of-id='Front'
      ${this.title || `title='${t(this.isVisaScope ? 'document.title.visa' : 'document.title.front')}'`} ${this.showNavigation} ${this.hideInstructions ? '' : 'hidden'} ${this.hideAttribution}
      ${this.documentCaptureModes} ${this.documentType} ${this.documentFormat} ${this.documentName} ${this.autoCapture} ${this.autoCaptureTimeout} theme-color='${this.themeColor}'
      ></${captureTag}>
      <${instructionsTag} id='document-capture-instructions-back' side-of-id='Back' title='${t('document.title.back')}'
       ${this.documentCaptureModes} ${this.documentType} ${this.documentFormat} ${this.documentName} ${this.showNavigation} ${this.instructionsThemeColor} ${this.hideAttribution} hidden
       ></${instructionsTag}>
      <${captureTag} id='document-capture-back' side-of-id='Back' ${this.title || `title='${t('document.title.back')}'`}  ${this.showNavigation}
      ${this.documentCaptureModes} ${this.documentType} ${this.documentFormat} ${this.documentName} ${this.autoCapture} ${this.autoCaptureTimeout} theme-color='${this.themeColor}' ${this.hideAttribution}
      hidden
      ></${captureTag}>
      <document-capture-review id='front-of-document-capture-review' theme-color='${this.themeColor}' ${this.showNavigation} ${this.hideAttribution} hidden></document-capture-review>
      <document-capture-review id='back-of-document-capture-review' theme-color='${this.themeColor}' ${this.showNavigation} ${this.hideAttribution} hidden></document-capture-review>
      </div>
    `;

    this._data = {
      images: [],
      meta: {
        libraryVersion: COMPONENTS_VERSION,
      },
    };

    this.documentInstruction = this.querySelector(
      '#document-capture-instructions-front',
    );
    this.documentInstructionBack = this.querySelector(
      '#document-capture-instructions-back',
    );
    this.idCapture = this.querySelector('#document-capture-front');
    this.idReview = this.querySelector('#front-of-document-capture-review');
    this.idCaptureBack = this.querySelector('#document-capture-back');
    this.backOfIdReview = this.querySelector(
      '#back-of-document-capture-review',
    );
    this.thankYouScreen = this.querySelector('thank-you');

    if (this.hideInstructions) {
      if (!this.autoCaptureFeature) {
        // <document-auto-capture> manages its own camera; calling
        // getPermissions here would race a second getUserMedia for the
        // rear camera and freeze the page.
        getPermissions(this.idCapture);
      }
      this.setActiveScreen(this.idCapture);
    } else {
      this.setActiveScreen(this.documentInstruction);
    }

    this.setUpEventListeners();
  }

  disconnectedCallback() {
    SmartCamera.stopMedia();
    smartCameraWeb?.dispatchEvent(new CustomEvent('metadata.cleanup'));
    if (this.activeScreen) {
      this.activeScreen.removeAttribute('hidden');
    }
    this.activeScreen = null;
    this.innerHTML = '';
  }

  setUpEventListeners() {
    this.documentInstruction.addEventListener(
      'document-capture-instructions.cancelled',
      () => {
        this.handleBackEvents();
      },
    );

    this.documentInstruction.addEventListener(
      'document-capture-instructions.capture',
      async () => {
        // The start event already seeds `camera_manual_capture` as the origin;
        // auto-capture overwrites it at publish time.
        this._dispatchCaptureMetadata('capture-start');
        this.setActiveScreen(this.idCapture);
        if (!this.autoCaptureFeature) await getPermissions(this.idCapture);
      },
    );
    this.documentInstruction.addEventListener(
      'document-capture-instructions.upload',
      async (event) => {
        this._dispatchCaptureMetadata('origin', { imageOrigin: 'gallery' });
        this.idReview.setAttribute('data-image', event.detail.previewImage);
        this._data.images.push({
          image: event.detail.image.split(',')[1],
          image_type_id: this.primaryImageType,
        });
        this.setActiveScreen(this.idReview);
      },
    );

    this.idCapture.addEventListener('document-capture.publish', (event) => {
      this._dispatchCaptureMetadata('capture-end');
      if (this.autoCaptureFeature) {
        this._dispatchCaptureMetadata('origin', {
          imageOrigin: event.detail.captureOrigin,
        });
      }
      this.idReview.setAttribute('data-image', event.detail.previewImage);
      this._data.images.push({
        image: event.detail.image.split(',')[1],
        image_type_id: this.primaryImageType,
      });
      SmartCamera.stopMedia();
      this.setActiveScreen(this.idReview);
    });

    this.idCapture.addEventListener('document-capture.cancelled', () => {
      if (this.hideInstructions) {
        this.handleBackEvents();
      } else {
        this.setActiveScreen(this.documentInstruction);
      }
    });

    this.idReview.addEventListener(
      'document-capture-review.rejected',
      async () => {
        this._dispatchCaptureMetadata('capture-retry');
        this.idReview.removeAttribute('data-image');
        this._data.images.pop();
        if (this.hideInstructions) {
          this.setActiveScreen(this.idCapture);
          if (!this.autoCaptureFeature) await getPermissions(this.idCapture);
        } else {
          this.setActiveScreen(this.documentInstruction);
        }
      },
    );

    this.idReview.addEventListener(
      'document-capture-review.accepted',
      async () => {
        if (this.hideBackOfId) {
          this._publishSelectedImages();
        } else if (this.hideInstructions) {
          this.setActiveScreen(this.idCaptureBack);
          if (!this.autoCaptureFeature)
            await getPermissions(this.idCaptureBack);
        } else {
          this.setActiveScreen(this.documentInstructionBack);
        }
      },
    );

    this.documentInstructionBack.addEventListener(
      'document-capture-instructions.capture',
      async () => {
        smartCameraWeb?.dispatchEvent(
          new CustomEvent('metadata.document-back-capture-start'),
        );
        this.setActiveScreen(this.idCaptureBack);
        if (!this.autoCaptureFeature) await getPermissions(this.idCaptureBack);
      },
    );

    this.documentInstructionBack.addEventListener(
      'document-capture-instructions.cancelled',
      async () => {
        this.idReview.removeAttribute('data-image');
        this._data.images.pop();
        if (this.hideInstructions) {
          this.setActiveScreen(this.idCapture);
          if (!this.autoCaptureFeature) await getPermissions(this.idCapture);
        } else {
          this.setActiveScreen(this.documentInstruction);
        }
      },
    );

    // "Skip" on the back instruction screen: the user chooses not to capture
    // the back of the document, so we publish with just the front image.
    this.documentInstructionBack.addEventListener(
      'document-capture-instructions.skip',
      () => {
        this._publishSelectedImages();
      },
    );

    this.documentInstructionBack.addEventListener(
      'document-capture-instructions.upload',
      async (event) => {
        smartCameraWeb?.dispatchEvent(
          new CustomEvent('metadata.document-back-origin', {
            detail: { imageOrigin: 'gallery' },
          }),
        );
        this.backOfIdReview.setAttribute('data-image', event.detail.image);
        this._data.images.push({
          image: event.detail.image.split(',')[1],
          image_type_id: IMAGE_TYPE.ID_CARD_BACK_IMAGE_BASE64,
        });
        this.setActiveScreen(this.backOfIdReview);
      },
    );
    this.idCaptureBack.addEventListener('document-capture.publish', (event) => {
      smartCameraWeb?.dispatchEvent(
        new CustomEvent('metadata.document-back-capture-end'),
      );
      if (this.autoCaptureFeature) {
        smartCameraWeb?.dispatchEvent(
          new CustomEvent('metadata.document-back-origin', {
            detail: { imageOrigin: event.detail.captureOrigin },
          }),
        );
      }
      this.backOfIdReview.setAttribute('data-image', event.detail.previewImage);
      this._data.images.push({
        image: event.detail.image.split(',')[1],
        image_type_id: IMAGE_TYPE.ID_CARD_BACK_IMAGE_BASE64,
      });
      this.setActiveScreen(this.backOfIdReview);
      SmartCamera.stopMedia();
    });

    this.idCaptureBack.addEventListener(
      'document-capture.cancelled',
      async () => {
        if (this.hideInstructions) {
          this.setActiveScreen(this.idCapture);
          if (!this.autoCaptureFeature) await getPermissions(this.idCapture);
        } else {
          this.setActiveScreen(this.documentInstructionBack);
        }
      },
    );

    this.backOfIdReview.addEventListener(
      'document-capture-review.rejected',
      async () => {
        smartCameraWeb?.dispatchEvent(
          new CustomEvent('metadata.document-back-capture-retry'),
        );
        this.backOfIdReview.removeAttribute('data-image');
        this._data.images.pop();
        if (this.hideInstructions) {
          this.setActiveScreen(this.idCaptureBack);
          if (!this.autoCaptureFeature)
            await getPermissions(this.idCaptureBack);
        } else {
          this.setActiveScreen(this.documentInstructionBack);
        }
      },
    );

    this.backOfIdReview.addEventListener(
      'document-capture-review.accepted',
      () => {
        this._publishSelectedImages();
      },
    );

    const screens = [
      this.documentInstruction,
      this.idCapture,
      this.documentInstructionBack,
      this.idCaptureBack,
      this.idReview,
      this.backOfIdReview,
    ];

    screens.forEach((screen) => {
      screen.addEventListener(`${screen.nodeName.toLowerCase()}.close`, () =>
        this.handleCloseEvents(),
      );
    });
  }

  _publishSelectedImages() {
    this.dispatchEvent(
      new CustomEvent('document-capture-screens.publish', {
        detail: this._data,
      }),
    );
  }

  /**
   * Dispatches a capture-metadata event for the *first* (and, for a visa, only)
   * side this instance captures, namespaced by `capture-scope`.
   *
   * A flow can mount this component more than once — Document Verification +
   * Visa runs a document instance followed by a visa instance — and both would
   * otherwise report into the same `document_front_*` metadata keys, so the
   * second silently overwrites the first. The scope keeps them apart.
   * Back-side events are always `document-back-*`: only the document scope has
   * a back side.
   * @param {string} suffix - `capture-start`, `capture-end`, `capture-retry` or `origin`.
   * @param {object} [detail]
   */
  _dispatchCaptureMetadata(suffix, detail) {
    smartCameraWeb?.dispatchEvent(
      new CustomEvent(
        `metadata.${this.isVisaScope ? 'visa' : 'document-front'}-${suffix}`,
        detail ? { detail } : undefined,
      ),
    );
  }

  get captureScope() {
    return this.getAttribute('capture-scope') === 'visa' ? 'visa' : 'document';
  }

  get isVisaScope() {
    return this.captureScope === 'visa';
  }

  /**
   * Image type for the first side captured. The visa is a document in its own
   * right rather than the front of one, so it carries its own id — the embed
   * reads it to pick the `visa` multipart field over `document`.
   */
  get primaryImageType() {
    return this.isVisaScope
      ? IMAGE_TYPE.VISA_IMAGE_BASE64
      : IMAGE_TYPE.ID_CARD_IMAGE_BASE64;
  }

  get hideInstructions() {
    return this.hasAttribute('hide-instructions');
  }

  get autoCaptureFeature() {
    return this.hasAttribute('auto-capture-enabled');
  }

  get autoCapture() {
    if (!this.hasAttribute('auto-capture')) {
      return '';
    }

    const value = this.getAttribute('auto-capture');
    const normalizedValue = value === '' ? 'autoCapture' : value;
    const allowedValues = [
      'autoCapture',
      'autoCaptureOnly',
      'manualCaptureOnly',
    ];

    if (allowedValues.includes(normalizedValue)) {
      return `auto-capture='${normalizedValue}'`;
    }

    return `auto-capture='autoCapture'`;
  }

  get autoCaptureTimeout() {
    const raw = this.getAttribute('auto-capture-timeout');
    const n = Number(raw);
    // Numeric milliseconds only — omit if not a finite positive number so an
    // arbitrary attribute value can't break/inject the template. The downstream
    // <document-auto-capture> clamps the value to its supported range.
    return raw !== null && Number.isFinite(n) && n > 0
      ? `auto-capture-timeout="${n}"`
      : '';
  }

  get hideBackOfId() {
    return this.hasAttribute('hide-back-of-id');
  }

  get showNavigation() {
    return this.hasAttribute('show-navigation') ? 'show-navigation' : '';
  }

  get title() {
    return this.hasAttribute('title')
      ? `title=${this.getAttribute('title')}`
      : '';
  }

  get documentCaptureModes() {
    return this.hasAttribute('document-capture-modes')
      ? `document-capture-modes='${this.getAttribute('document-capture-modes')}'`
      : '';
  }

  get documentType() {
    return this.hasAttribute('document-type')
      ? `document-type='${this.getAttribute('document-type')}'`
      : '';
  }

  get documentFormat() {
    return this.hasAttribute('document-format')
      ? `document-format='${this.getAttribute('document-format')}'`
      : '';
  }

  // The catalog's localised display name for the selected document, e.g.
  // "Green Book" rather than the parent's `IDENTITY_CARD` code.
  get documentName() {
    return this.hasAttribute('document-name')
      ? `document-name="${escAttr(this.getAttribute('document-name'))}"`
      : '';
  }

  get hideAttribution() {
    return this.hasAttribute('hide-attribution') ? 'hide-attribution' : '';
  }

  get themeColor() {
    return this.getAttribute('theme-color') || '#001096';
  }

  // The instructions screen has its own default colour, so it only receives a
  // theme-color the host actually set.
  get instructionsThemeColor() {
    const themeColor = this.getAttribute('theme-color');
    return themeColor ? `theme-color='${themeColor}'` : '';
  }

  handleBackEvents() {
    this.dispatchEvent(new CustomEvent('document-capture-screens.cancelled'));
  }

  handleCloseEvents() {
    this.dispatchEvent(new CustomEvent('document-capture-screens.close'));
  }

  setActiveScreen(screen) {
    this.activeScreen?.setAttribute('hidden', '');
    screen.removeAttribute('hidden');
    this.activeScreen = screen;
  }

  static get observedAttributes() {
    return [
      'capture-scope',
      'document-capture-modes',
      'document-format',
      'document-name',
      'document-type',
      'hide-back-to-host',
      'show-navigation',
      'hide-back-of-id',
      'auto-capture-enabled',
      'auto-capture',
      'auto-capture-timeout',
    ];
  }

  attributeChangedCallback(name) {
    switch (name) {
      case 'capture-scope':
      case 'document-capture-modes':
      case 'document-format':
      case 'document-name':
      case 'document-type':
      case 'hide-back-of-id':
      case 'hide-back-to-host':
      case 'show-navigation':
      case 'auto-capture-enabled':
      case 'auto-capture':
      case 'auto-capture-timeout':
        this.innerHTML = '';
        this.connectedCallback();
        break;
      default:
        break;
    }
  }
}

if (
  'customElements' in window &&
  !customElements.get('document-capture-screens')
) {
  customElements.define('document-capture-screens', DocumentCaptureScreens);
}

export default DocumentCaptureScreens;
